import type { Datum, Series } from '#client/components/charts';
import type {
  AnalyticsSeries,
  CategoryDatum,
  Distribution,
  Histogram,
  OnTimeSplit,
  OrderFunnel,
  QualityReport,
  StaffRow,
  SupplyRow,
} from '#shared/types';

/**
 * Wire shapes to chart shapes.
 *
 * A thin adapter, and deliberately the only one. The server sends chart-ready
 * data — bucketed, zero-filled, labelled — so this renames fields and does no
 * arithmetic. The moment a page starts computing a share or a total here, two
 * screens have two answers.
 */

export function toSeries(series: AnalyticsSeries): Series {
  return {
    id: series.key,
    label: series.label,
    points: series.points.map((point): Datum => ({
      key: point.bucket,
      label: point.label,
      value: point.value,
    })),
  };
}

/**
 * The comparison run, as its own series.
 *
 * Keyed on the *current* period's buckets, not its own, because the two windows
 * have different dates and a chart with two x-axes is not a chart. The label
 * says which period it is, so the alignment is explained rather than implied.
 */
export function toCompareSeries(series: AnalyticsSeries, label: string): Series | null {
  if (series.compare.length === 0) return null;

  return {
    id: `${series.key}_compare`,
    label,
    points: series.compare.map((point, index) => ({
      key: series.points[index]?.bucket ?? String(index),
      // The comparison point's own date, so a tooltip can say which day it was.
      label: point.label,
      value: point.value,
    })),
  };
}

export function toBarSeries(id: string, label: string, rows: readonly CategoryDatum[]): Series {
  return {
    id,
    label,
    points: rows.map((row): Datum => ({
      key: row.key,
      label: row.label,
      value: row.value,
      ...(row.href === null ? {} : { href: row.href }),
    })),
  };
}

export function toDonutPoints(rows: readonly CategoryDatum[]): Datum[] {
  return rows.map((row) => ({
    key: row.key,
    label: row.label,
    value: row.value,
    ...(row.href === null ? {} : { href: row.href }),
  }));
}

/** The current run, with the comparison run beside it when there is one. */
export function withComparison(series: AnalyticsSeries, compare: Series | null): Series[] {
  return compare ? [toSeries(series), compare] : [toSeries(series)];
}

/**
 * A distribution as three bars: median, p90 and the slowest single case — the
 * three numbers a lab argues about.
 */
export function toSpreadSeries(spread: Distribution): Series {
  return {
    id: 'spread',
    label: 'Hours',
    points: [
      { key: 'p50', label: 'Half within', value: spread.p50 },
      { key: 'p90', label: '9 in 10 within', value: spread.p90 },
      { key: 'max', label: 'Slowest', value: spread.max },
    ],
  };
}

export function toOrderOutcomeSeries(orders: OrderFunnel): Series {
  return {
    id: 'outcome',
    label: 'Requests',
    points: [
      { key: 'registered', label: 'Registered', value: orders.registered },
      { key: 'pending', label: 'Still waiting', value: orders.pending },
      { key: 'declined', label: 'Set aside', value: orders.declined },
    ],
  };
}

export function toOnTimeSeries(split: OnTimeSplit): Series[] {
  return [
    {
      id: 'on_time',
      label: 'On time',
      // A genuine status, so it takes a status colour rather than a
      // categorical one — the one case `tone` exists for.
      tone: 'success',
      points: [{ key: 'all', label: 'Released', value: split.on_time }],
    },
    {
      id: 'late',
      label: 'Late',
      tone: 'critical',
      points: [{ key: 'all', label: 'Released', value: split.late }],
    },
  ];
}

export function toFlagPoints(flags: QualityReport['flags']): Datum[] {
  return [
    { key: 'normal', label: 'Within range', value: flags.normal },
    { key: 'high', label: 'Above range', value: flags.high },
    { key: 'low', label: 'Below range', value: flags.low },
    // Given a slice of its own, not folded into "within range". Its size is
    // the finding.
    { key: 'not_judgeable', label: 'Not judgeable', value: flags.not_judgeable },
  ];
}

export function toHistogramSeries(histogram: Histogram): Series {
  return {
    id: 'count',
    label: 'Results',
    points: histogram.bins.map((bin) => ({
      key: String(bin.from),
      label: String(bin.from),
      value: bin.count,
    })),
  };
}

export function toStaffOutputSeries(rows: readonly StaffRow[]): Series[] {
  const pointsFor = (pick: (row: StaffRow) => number): Datum[] =>
    rows.map((row) => ({ key: String(row.user_id), label: row.name, value: pick(row) }));

  return [
    { id: 'performed', label: 'Results entered', points: pointsFor((row) => row.performed) },
    { id: 'verified', label: 'Reports verified', points: pointsFor((row) => row.verified) },
  ];
}

export function toConsumptionSeries(rows: readonly SupplyRow[]): Series {
  return toBarSeries(
    'consumed',
    'Consumed',
    rows.map((row) => ({
      key: String(row.item_id),
      label: `${row.name} (${row.unit})`,
      value: row.consumed_base,
      share: 0,
      secondary: row.cost,
      href: row.href,
    })),
  );
}
